import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {expenseActions} from '../../state/actions/expense.actions';
import {expensesHelpers, tabHelpers} from '../../utils/helpers';
import {DEFAULT_PAGE_SIZE, expenseReviewStatusConstants} from '../../constants';

const DEFAULT_ORDER_BY = '-date';

const FIXED_QUERY = {
  review_status: [expenseReviewStatusConstants.IN_REVIEW],
  per_page: DEFAULT_PAGE_SIZE,
};

export const useToReviewTab = ({query = {}, enabled = true} = {}) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const loadedRef = useRef(false);
  const paginationRef = useRef(null);
  const rowsRef = useRef([]);

  useEffect(() => {paginationRef.current = pagination;}, [pagination]);
  useEffect(() => {rowsRef.current = rows;}, [rows]);

  const effectiveQuery = useMemo(() => {
    const {month, year, date_field, order_by: incomingOrderBy, per_page: _pp, review_status: _rs, ...rest} = query;
    const dateFilterValues = expensesHelpers.getExpenseListFilterValues({month, year, date_field});
    const extraFilters = Object.fromEntries(Object.entries(rest).filter(([, v]) => v != null));
    // This tab normally owns its own ordering, which is why the incoming `order_by` is dropped.
    // An active amount filter is the exception: the shared filter bar decides the order then, so
    // the list is sorted by price like every other tab rather than sitting on date while the
    // filter is plainly applied (H1MVP-4213).
    const hasAmountFilter = rest.low_value != null || rest.high_value != null;
    return {
      ...FIXED_QUERY,
      order_by: hasAmountFilter && incomingOrderBy ? incomingOrderBy : orderBy,
      ...(dateFilterValues ? dateFilterValues : {month, year}),
      ...extraFilters,
    };
  }, [query, orderBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveQueryRef = useRef(effectiveQuery);
  useEffect(() => {effectiveQueryRef.current = effectiveQuery;}, [effectiveQuery]);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    dispatch(expenseActions.getExpenses({
      query: effectiveQuery,
      successCallback: (response) => {
        setRows(response.results);
        setPagination(response.pagination ?? null);
        loadedRef.current = true;
        setLoading(false);
        setSelectedRowKeys(prev => prev.length > 0 ? [] : prev);
      },
      errorCallback: () => setLoading(false),
    }));
  }, [dispatch, effectiveQuery, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(() => {
    if (!loadedRef.current) return;
    dispatch(expenseActions.getExpenses({
      query: effectiveQueryRef.current,
      successCallback: (response) => {
        setRows(response.results);
        setPagination(response.pagination ?? null);
        setSelectedRowKeys(prev => prev.length > 0 ? [] : prev);
      },
    }));
  }, [dispatch]);

  const updateRows = useCallback((expenses) => {
    const byId = Object.fromEntries(expenses.map(e => [e.id, e]));
    setRows(prev => prev.map(row => byId[row.id] ?? row));
  }, []);

  const updateRow = useCallback(({id, response}) => {
    const belongsToTab = FIXED_QUERY.review_status.includes(response?.review_status);
    const isInList = rowsRef.current.some(r => r.id === id);

    if (!isInList && belongsToTab && loadedRef.current) {
      dispatch(expenseActions.getExpenses({
        query: effectiveQueryRef.current,
        successCallback: (res) => {
          setRows(res.results);
          setPagination(res.pagination ?? null);
          setSelectedRowKeys(prev => prev.length > 0 ? [] : prev);
        },
      }));
      return;
    }

    if (!isInList) return;

    setRows(prev => belongsToTab
      ? prev.map(r => r.id === id ? response : r)
      : prev.filter(r => r.id !== id)
    );
  }, [dispatch]);

  const loadMore = useCallback(() => {
    if (!paginationRef.current?.cursor) return;
    dispatch(expenseActions.getExpenses({
      query: {...effectiveQueryRef.current, cursor: paginationRef.current.cursor},
      successCallback: (response) => {
        setRows(prev => [...prev, ...response.results]);
        setPagination(response.pagination ?? null);
      },
    }));
  }, [dispatch]);

  const removeRows = useCallback((ids) => {
    setRows(prev => prev.filter(r => !ids.includes(r.id)));
  }, []);

  const prependRows = useCallback((expenses) => {
    setRows(prev => [...expenses, ...prev]);
  }, []);

  const handleTableChange = useCallback((_pagination, _filters, sorter, extra) => {
    tabHelpers.handleOnTableChange({
      view: 'to_review',
      sorter,
      extra,
      loadDataFunc: ({query: sortQuery}) => setOrderBy(sortQuery.order_by ?? DEFAULT_ORDER_BY),
    });
  }, []);

  return {
    rows, loading, pagination, selectedRowKeys,
    setSelectedRowKeys, updateRow, updateRows, refresh, loadMore, removeRows, prependRows, handleTableChange,
  };
};
